//============================================================================================================
// Detects if a payload most likely represents an API failure.
// Works with strings, typical { succeeded:false }, { errors: ... },
// and bodies that include status/statusCode fields.
//============================================================================================================
export const isFailurePayload = (res) => {
  // Prefer explicit metadata added by BufferService
  if (res && typeof res === 'object') {
    if (res.__ok === true) return false
    if (typeof res.__status === 'number') return res.__status >= 400
    if (res.__isApiError) return true
  }

  if (!res) return true
  if (typeof res === 'string') return true
  if (res.succeeded === false || res.Succeeded === false) return true

  // errors may be array, object or string
  if (Array.isArray(res.errors) || Array.isArray(res.Errors)) return true
  if (res.errors && typeof res.errors === 'object') return true
  if (typeof res.errors === 'string' || typeof res.Errors === 'string') return true

  // body-level status fields commonly returned on error
  if (typeof res.status === 'number' && res.status >= 400) return true
  if (typeof res.statusCode === 'number' && res.statusCode >= 400) return true
  if (typeof res.StatusCode === 'number' && res.StatusCode >= 400) return true

  return false
}

//============================================================================================================
// Extracts the most useful error message from a variety of API shapes.
// Prefers BufferService's hidden __message when present, then dives into
// response.data, common fields, arrays, and object maps.
//============================================================================================================
export const apiMessage = (obj, fallback = "An error occurred.") => {
  if (!obj) return fallback

  // Prefer message attached by BufferService
  if (obj.__message) return obj.__message

  // Axios-like errors: payload can be at response.data or data
  const payload =
    obj?.response?.data !== undefined
      ? obj.response.data
      : (obj?.data !== undefined ? obj.data : obj)

  // If API returned a raw string (ex: "Username too long.")
  if (typeof payload === 'string') return payload

  // Try common fields (various casings and aliases)
  const msg =
    payload?.message ||
    payload?.Message ||
    payload?.msg ||
    payload?.title ||
    payload?.detail ||
    (typeof payload?.error === 'string' ? payload.error : null) ||
    // Arrays of errors
    (Array.isArray(payload?.errors) && payload.errors.filter(Boolean).join(', ')) ||
    (Array.isArray(payload?.Errors) && payload.Errors.filter(Boolean).join(', ')) ||
    // Object maps of errors: { field: ["err1","err2"], ... }
    (payload?.errors && typeof payload.errors === 'object'
      ? Object.values(payload.errors).flat().filter(Boolean).join(', ')
      : null) ||
    (payload?.Errors && typeof payload.Errors === 'object'
      ? Object.values(payload.Errors).flat().filter(Boolean).join(', ')
      : null)

  if (msg) return msg

  // Fallback to formatted status when available
  const status =
    obj.__status ?? // prefer metadata from BufferService
    obj?.response?.status ??
    payload?.status ??
    payload?.statusCode ??
    payload?.StatusCode

  const statusText = obj?.response?.statusText
  if (status && statusText) return `${status} ${statusText}`
  if (status) return `${status}`

  return fallback
}
